import { useState, useEffect } from 'react';
import type { Language } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import { supabase } from '../../lib/supabase';

interface AuthPageProps {
  lang: Language;
  /** Called after a successful login — optional so unit tests can omit it. */
  onLogin?: () => void;
  onToggleLang: () => void;
}

type AuthMode = 'signin' | 'signup' | 'forgot' | 'sent-confirm' | 'sent-reset';

const S = {
  wrap: {
    minHeight: '100dvh',
    display: 'flex',
    backgroundColor: 'var(--bg-base)',
  },
  hero: {
    flex: 1,
    position: 'relative' as const,
    overflow: 'hidden',
    padding: 40,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
    backgroundColor: 'var(--bg-surface)',
    backgroundImage:
      'radial-gradient(ellipse at 30% 20%, color-mix(in oklch, var(--accent) 18%, transparent), transparent 55%), radial-gradient(ellipse at 80% 80%, color-mix(in oklch, var(--accent) 12%, transparent), transparent 55%)',
  },
  heroGrid: {
    position: 'absolute' as const,
    inset: 0,
    backgroundImage:
      'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
    backgroundSize: '56px 56px',
    maskImage: 'radial-gradient(ellipse at center, black, transparent 70%)',
    WebkitMaskImage: 'radial-gradient(ellipse at center, black, transparent 70%)',
    opacity: 0.4,
    pointerEvents: 'none' as const,
  },
  heroContent: { position: 'relative' as const, zIndex: 1, maxWidth: 460 },
  heroBrand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36 },
  heroBrandMark: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background:
      'linear-gradient(135deg, var(--accent) 0%, var(--accent) 50%, transparent 50%), oklch(95% 0.005 var(--hue))',
    color: 'oklch(20% 0.012 var(--hue))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: '-0.04em',
  },
  heroH1: {
    fontSize: 40,
    fontWeight: 600,
    letterSpacing: '-0.025em',
    lineHeight: 1.05,
    margin: '0 0 14px',
    color: 'var(--text-primary)',
  },
  heroBody: { color: 'var(--text-muted)', fontSize: 15, maxWidth: '38ch', margin: 0, lineHeight: 1.55 },
  heroStats: {
    position: 'relative' as const,
    zIndex: 1,
    borderTop: '1px solid var(--border)',
    paddingTop: 18,
    fontSize: 12,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-muted)',
    display: 'flex',
    gap: 30,
    flexWrap: 'wrap' as const,
  },
  formPane: {
    width: 480,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderLeft: '1px solid var(--border)',
    position: 'relative' as const,
  },
  langToggle: {
    position: 'absolute' as const,
    top: 20,
    right: 20,
    padding: '6px 12px',
    border: '1px solid var(--border)',
    borderRadius: 8,
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    fontWeight: 600,
    zIndex: 2,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 24,
  },
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 8,
    textAlign: 'center' as const,
  },
  logo: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background:
      'linear-gradient(135deg, var(--accent) 0%, var(--accent) 50%, transparent 50%), oklch(95% 0.005 var(--hue))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'oklch(20% 0.012 var(--hue))',
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: '-0.04em',
    marginBottom: 8,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--text-muted)',
    margin: 0,
  },
  googleBtn: {
    width: '100%',
    padding: '11px 16px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    backgroundColor: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    transition: 'background 0.12s',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: 'var(--text-muted)',
    fontSize: 12,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'var(--border)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
  label: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 5,
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  input: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.12s',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  submitBtn: {
    width: '100%',
    padding: '11px 16px',
    borderRadius: 10,
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.12s',
    marginTop: 4,
  },
  errorBox: {
    padding: '10px 14px',
    borderRadius: 8,
    backgroundColor: 'var(--danger-muted, #fff0f0)',
    border: '1px solid var(--danger, #e53e3e)',
    color: 'var(--danger, #e53e3e)',
    fontSize: 13,
  },
  footer: {
    textAlign: 'center' as const,
    fontSize: 13,
    color: 'var(--text-muted)',
  },
  link: {
    color: 'var(--accent)',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 500,
  },
  forgotLink: {
    textAlign: 'right' as const,
    fontSize: 12,
    color: 'var(--accent)',
    cursor: 'pointer',
    fontWeight: 500,
    marginTop: -4,
  },
  sentIcon: {
    fontSize: 48,
    textAlign: 'center' as const,
  },
  sentTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
    textAlign: 'center' as const,
  },
  sentBody: {
    fontSize: 14,
    color: 'var(--text-muted)',
    textAlign: 'center' as const,
    lineHeight: 1.5,
  },
  sentEmail: {
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  backBtn: {
    width: '100%',
    padding: '11px 16px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
};

/** Split-hero frame: marketing panel on the left (desktop), form card on the right. */
function Frame({
  lang,
  onToggleLang,
  children,
}: {
  lang: Language;
  onToggleLang: () => void;
  children: React.ReactNode;
}) {
  const i = I18N[lang].auth;
  const [wide, setWide] = useState(() => window.innerWidth >= 860);
  useEffect(() => {
    const handler = () => setWide(window.innerWidth >= 860);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div style={S.wrap}>
      {wide && (
        <div style={S.hero}>
          <div style={S.heroGrid} aria-hidden="true" />
          <div style={S.heroContent}>
            <div style={S.heroBrand}>
              <div style={S.heroBrandMark}>S</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>Sourcr</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                  v1.0 · MVP
                </div>
              </div>
            </div>
            <h1 style={S.heroH1}>{i.heroTitle}</h1>
            <p style={S.heroBody}>{i.heroBody}</p>
          </div>
          <div style={S.heroStats}>
            <span>{i.stat1}</span>
            <span>{i.stat2}</span>
            <span>{i.stat3}</span>
          </div>
        </div>
      )}
      <div style={wide ? S.formPane : { ...S.formPane, width: '100%', borderLeft: 'none' }}>
        <button type="button" onClick={onToggleLang} style={S.langToggle}>
          {lang === 'en' ? 'EN' : 'УК'}
        </button>
        {children}
      </div>
    </div>
  );
}

export function AuthPage({ lang, onLogin, onToggleLang }: AuthPageProps) {
  const i = I18N[lang].auth;
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Email context passed to sent-* modes
  const [sentEmail, setSentEmail] = useState('');

  function resetForm() {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setLoading(false);
  }

  function goTo(next: AuthMode) {
    resetForm();
    setMode(next);
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError) {
      setError(authError.message);
      setLoading(false);
    } else {
      onLogin?.();
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(i.passwordTooShort);
      return;
    }
    if (password !== confirmPassword) {
      setError(i.passwordMismatch);
      return;
    }

    setLoading(true);
    const { error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin + '/auth/callback',
      },
    });

    if (authError) {
      setError(authError.message);
      setLoading(false);
    } else {
      setSentEmail(email);
      setMode('sent-confirm');
    }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: authError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/auth/reset-password',
    });

    if (authError) {
      setError(authError.message);
      setLoading(false);
    } else {
      setSentEmail(email);
      setMode('sent-reset');
    }
  }

  async function handleGoogle() {
    setLoading(true);
    setError(null);

    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });

    if (authError) {
      // Supabase returns "Unsupported provider: provider is not enabled" when the
      // Google provider hasn't been configured in the project. Show a friendly
      // nudge to the email option instead of the raw error.
      const providerDisabled = /provider is not enabled|unsupported provider/i.test(authError.message);
      setError(providerDisabled ? i.googleNotEnabled : authError.message);
      setLoading(false);
    }
    // On success the browser is redirected; no further action needed here.
  }

  const googleSvg = (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.59.102-1.167.282-1.707V4.961H.957C.347 6.173 0 7.548 0 9s.347 2.827.957 4.039l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z" />
    </svg>
  );

  // ── Sent (email check) modes ────────────────────────────────────────────────
  if (mode === 'sent-confirm' || mode === 'sent-reset') {
    const bodyText = mode === 'sent-confirm' ? i.checkEmailConfirm : i.checkEmailReset;
    return (
      <Frame lang={lang} onToggleLang={onToggleLang}>
        <div style={S.card}>
          <div style={S.sentIcon}>📧</div>
          <h1 style={S.sentTitle}>{i.checkEmail}</h1>
          <p style={S.sentBody}>
            {bodyText}{' '}
            <span style={S.sentEmail}>{sentEmail}</span>
          </p>
          <button type="button" onClick={() => goTo('signin')} style={S.backBtn}>
            ← {i.backToSignIn}
          </button>
        </div>
      </Frame>
    );
  }

  // ── Forgot password mode ────────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <Frame lang={lang} onToggleLang={onToggleLang}>
        <div style={S.card}>
          <div style={S.header}>
            <div style={S.logo}>Sr</div>
            <h1 style={S.title}>{i.resetPasswordTitle}</h1>
            <p style={S.subtitle}>{i.resetPasswordDesc}</p>
          </div>

          {error !== null && <div style={S.errorBox}>{error}</div>}

          <form onSubmit={handleForgot} style={S.form}>
            <label style={S.label}>
              {i.email}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                style={S.input}
                autoComplete="email"
              />
            </label>
            <button type="submit" disabled={loading} style={S.submitBtn}>
              {loading ? '...' : i.sendResetLink}
            </button>
          </form>

          <p style={S.footer}>
            <span style={S.link} role="button" tabIndex={0} onClick={() => goTo('signin')}
              onKeyDown={(e) => { if (e.key === 'Enter') goTo('signin'); }}>
              ← {i.backToSignIn}
            </span>
          </p>
        </div>
      </Frame>
    );
  }

  // ── Sign Up mode ────────────────────────────────────────────────────────────
  if (mode === 'signup') {
    return (
      <Frame lang={lang} onToggleLang={onToggleLang}>
        <div style={S.card}>
          <div style={S.header}>
            <div style={S.logo}>Sr</div>
            <h1 style={S.title}>{i.createAccount}</h1>
            <p style={S.subtitle}>{i.subtitle}</p>
          </div>

          {error !== null && <div style={S.errorBox}>{error}</div>}

          {/* Google OAuth */}
          <button type="button" onClick={handleGoogle} disabled={loading} style={S.googleBtn}>
            {googleSvg}
            {i.continueWith} {i.google}
          </button>

          {/* Divider */}
          <div style={S.divider}>
            <div style={S.dividerLine} />
            <span>{i.orEmail}</span>
            <div style={S.dividerLine} />
          </div>

          <form onSubmit={handleSignUp} style={S.form}>
            <label style={S.label}>
              {i.email}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                style={S.input}
                autoComplete="email"
              />
            </label>
            <label style={S.label}>
              {i.password}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                style={S.input}
                autoComplete="new-password"
              />
            </label>
            <label style={S.label}>
              {i.confirmPassword}
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={S.input}
                autoComplete="new-password"
              />
            </label>
            <button type="submit" disabled={loading} style={S.submitBtn}>
              {loading ? '...' : i.createAccount}
            </button>
          </form>

          <p style={S.footer}>
            {i.alreadyHaveAccount}{' '}
            <span
              style={S.link}
              role="button"
              tabIndex={0}
              onClick={() => goTo('signin')}
              onKeyDown={(e) => { if (e.key === 'Enter') goTo('signin'); }}
            >
              {i.signIn}
            </span>
          </p>
        </div>
      </Frame>
    );
  }

  // ── Sign In mode (default) ──────────────────────────────────────────────────
  return (
    <Frame lang={lang} onToggleLang={onToggleLang}>
      <div style={S.card}>
        {/* Header */}
        <div style={S.header}>
          <div style={S.logo}>Sr</div>
          <h1 style={S.title}>{i.title}</h1>
          <p style={S.subtitle}>{i.subtitle}</p>
        </div>

        {/* Error banner */}
        {error !== null && <div style={S.errorBox}>{error}</div>}

        {/* Google OAuth */}
        <button type="button" onClick={handleGoogle} disabled={loading} style={S.googleBtn}>
          {googleSvg}
          {i.continueWith} {i.google}
        </button>

        {/* Divider */}
        <div style={S.divider}>
          <div style={S.dividerLine} />
          <span>{i.orEmail}</span>
          <div style={S.dividerLine} />
        </div>

        {/* Email/password form */}
        <form onSubmit={handleSignIn} style={S.form}>
          <label style={S.label}>
            {i.email}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              style={S.input}
              autoComplete="email"
            />
          </label>
          <label style={S.label}>
            {i.password}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={S.input}
              autoComplete="current-password"
            />
          </label>
          <div
            style={S.forgotLink}
            role="button"
            tabIndex={0}
            onClick={() => goTo('forgot')}
            onKeyDown={(e) => { if (e.key === 'Enter') goTo('forgot'); }}
          >
            {i.forgotPassword}
          </div>
          <button type="submit" disabled={loading} style={S.submitBtn}>
            {loading ? '...' : i.signIn}
          </button>
        </form>

        {/* Footer */}
        <p style={S.footer}>
          {i.noAccount}{' '}
          <span
            style={S.link}
            role="button"
            tabIndex={0}
            onClick={() => goTo('signup')}
            onKeyDown={(e) => { if (e.key === 'Enter') goTo('signup'); }}
          >
            {i.signUp}
          </span>
        </p>
      </div>
    </Frame>
  );
}
